<template>
  <div class="sku-input-container">
    <!-- SKU Generation Preview -->
    <div v-if="productName && autoGenerate" class="sku-preview">
      <label class="sku-preview-label">
        <Icon name="magic-wand" size="16" />
        Auto-generated SKU:
      </label>
      <div class="sku-preview-value">
        <code>{{ suggestedSku || 'Generating...' }}</code>
        <button 
          v-if="suggestedSku" 
          @click="useSuggestedSku"
          class="btn-use-suggested"
          :disabled="loading"
        >
          Use This SKU
        </button>
      </div>
    </div>

    <!-- Manual SKU Input -->
    <div class="sku-input-group">
      <label for="sku-input" class="sku-label">
        <Icon name="tag" size="16" />
        Product SKU
        <span v-if="required" class="required">*</span>
      </label>
      
      <div class="sku-input-wrapper">
        <input
          id="sku-input"
          v-model="localSku"
          type="text"
          placeholder="Enter SKU manually or use auto-generated"
          class="sku-input"
          :class="{
            'error': hasError,
            'success': isValid && localSku,
            'loading': checking
          }"
          :disabled="disabled || loading"
          @input="onSkuChange"
          @blur="validateSku"
          @focus="onFocus"
          maxlength="50"
        />
        
        <!-- Validation Status Icon -->
        <div class="validation-status">
          <LoadingSpinner v-if="checking" size="small" />
          <Icon v-else-if="isValid && localSku" name="check-circle" class="success-icon" />
          <Icon v-else-if="hasError" name="x-circle" class="error-icon" />
        </div>
      </div>

      <!-- SKU Format Helper -->
      <div class="sku-format-helper">
        <small>Format: WORD_WORD_NUMBER (e.g., COCA_ZERO_1)</small>
      </div>

      <!-- Validation Message -->
      <div v-if="validationMessage" class="validation-message" :class="messageType">
        <Icon :name="messageType === 'error' ? 'alert-triangle' : 'info'" size="14" />
        {{ validationMessage }}
      </div>

      <!-- SKU Suggestions (when typing) -->
      <div v-if="showSuggestions && suggestions.length > 0" class="sku-suggestions">
        <div class="suggestions-header">
          <Icon name="lightbulb" size="14" />
          Suggestions:
        </div>
        <button
          v-for="suggestion in suggestions"
          :key="suggestion.value"
          @click="selectSuggestion(suggestion.value)"
          class="suggestion-item"
        >
          <code>{{ suggestion.value }}</code>
          <small>{{ suggestion.label }}</small>
        </button>
      </div>
    </div>

    <!-- SKU Analytics (optional) -->
    <div v-if="showAnalytics && skuAnalytics" class="sku-analytics">
      <details>
        <summary>SKU Analytics</summary>
        <div class="analytics-content">
          <div class="analytics-item">
            <span>Total SKUs:</span>
            <span>{{ skuAnalytics.totalSKUs }}</span>
          </div>
          <div class="analytics-item">
            <span>Pattern matches:</span>
            <span>{{ skuAnalytics.patternMatches || 0 }}</span>
          </div>
        </div>
      </details>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import LoadingSpinner from './LoadingSpinner.vue'
import Icon from './Icon.vue'

export default {
  name: 'SkuInput',
  
  components: {
    LoadingSpinner,
    Icon
  },
  
  props: {
    // The product name to generate SKU from
    productName: {
      type: String,
      default: ''
    },
    
    // Initial SKU value
    modelValue: {
      type: String,
      default: ''
    },
    
    // Whether to auto-generate SKU from product name
    autoGenerate: {
      type: Boolean,
      default: true
    },
    
    // Whether SKU is required
    required: {
      type: Boolean,
      default: true
    },
    
    // Whether input is disabled
    disabled: {
      type: Boolean,
      default: false
    },
    
    // Show SKU analytics
    showAnalytics: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['update:modelValue', 'validation-change', 'sku-selected'],
  
  data() {
    return {
      localSku: this.modelValue,
      suggestedSku: '',
      isValid: false,
      hasError: false,
      checking: false,
      loading: false,
      validationMessage: '',
      messageType: 'info',
      suggestions: [],
      showSuggestions: false,
      skuAnalytics: null,
      debounceTimer: null,
      suggestionTimer: null
    }
  },
  
  watch: {
    modelValue(newValue) {
      this.localSku = newValue
    },
    
    productName(newName) {
      if (newName && this.autoGenerate) {
        this.generateSkuSuggestion(newName)
      }
    },
    
    localSku(newSku) {
      this.$emit('update:modelValue', newSku)
      this.debouncedValidation(newSku)
    }
  },
  
  mounted() {
    if (this.productName && this.autoGenerate) {
      this.generateSkuSuggestion(this.productName)
    }
    
    if (this.localSku) {
      this.validateSku()
    }
  },
  
  methods: {
    // Generate SKU suggestion from product name
    async generateSkuSuggestion(name) {
      if (!name || name.length < 2) return
      
      try {
        this.loading = true
        const response = await api.post('/products/generate-sku', {
          name: name.trim(),
          preferredSku: null
        })
        
        this.suggestedSku = response.data.suggestedSku
      } catch (error) {
        console.error('SKU generation error:', error)
        this.suggestedSku = ''
      } finally {
        this.loading = false
      }
    },
    
    // Use the suggested SKU
    useSuggestedSku() {
      if (this.suggestedSku) {
        this.localSku = this.suggestedSku
        this.validateSku()
        this.$emit('sku-selected', this.suggestedSku)
      }
    },
    
    // Handle SKU input changes
    onSkuChange() {
      this.clearValidation()
      this.getSuggestions()
    },
    
    // Handle input focus
    onFocus() {
      if (this.localSku && this.localSku.length > 1) {
        this.getSuggestions()
      }
    },
    
    // Get SKU suggestions while typing
    getSuggestions() {
      if (this.suggestionTimer) {
        clearTimeout(this.suggestionTimer)
      }
      
      this.suggestionTimer = setTimeout(async () => {
        if (this.localSku && this.localSku.length > 1) {
          try {
            const response = await api.get('/search/suggestions', {
              params: {
                q: this.localSku,
                type: 'sku',
                limit: 5
              }
            })
            
            this.suggestions = response.data.suggestions || []
            this.showSuggestions = this.suggestions.length > 0
          } catch (error) {
            console.error('Suggestions error:', error)
            this.suggestions = []
            this.showSuggestions = false
          }
        } else {
          this.suggestions = []
          this.showSuggestions = false
        }
      }, 300)
    },
    
    // Select a suggestion
    selectSuggestion(sku) {
      this.localSku = sku
      this.showSuggestions = false
      this.validateSku()
      this.$emit('sku-selected', sku)
    },
    
    // Debounced validation
    debouncedValidation(sku) {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }
      
      this.debounceTimer = setTimeout(() => {
        if (sku && sku.length > 2) {
          this.validateSku()
        }
      }, 500)
    },
    
    // Validate SKU format and availability
    async validateSku() {
      const sku = this.localSku.trim()
      
      if (!sku) {
        if (this.required) {
          this.setError('SKU is required')
        } else {
          this.clearValidation()
        }
        return
      }
      
      // Format validation
      const skuPattern = /^[A-Z0-9]+(_[A-Z0-9]+)*_\d+$/
      if (!skuPattern.test(sku)) {
        this.setError('Invalid SKU format. Use format: WORD_WORD_NUMBER')
        return
      }
      
      // Availability check
      try {
        this.checking = true
        const response = await api.get(`/products/sku-availability/${encodeURIComponent(sku)}`)
        
        if (response.data.available) {
          this.setSuccess('SKU is available')
        } else {
          this.setError('SKU already exists for this user')
        }
      } catch (error) {
        console.error('SKU availability check error:', error)
        this.setError('Could not verify SKU availability')
      } finally {
        this.checking = false
      }
    },
    
    // Set validation success state
    setSuccess(message) {
      this.isValid = true
      this.hasError = false
      this.validationMessage = message
      this.messageType = 'success'
      this.$emit('validation-change', { isValid: true, message, sku: this.localSku })
    },
    
    // Set validation error state
    setError(message) {
      this.isValid = false
      this.hasError = true
      this.validationMessage = message
      this.messageType = 'error'
      this.$emit('validation-change', { isValid: false, message, sku: this.localSku })
    },
    
    // Clear validation state
    clearValidation() {
      this.isValid = false
      this.hasError = false
      this.validationMessage = ''
      this.messageType = 'info'
      this.showSuggestions = false
    }
  }
}
</script>

<style scoped>
.sku-input-container {
  margin-bottom: 1rem;
}

.sku-preview {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}

.sku-preview-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6c757d;
  margin-bottom: 8px;
}

.sku-preview-value {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sku-preview-value code {
  background: #e9ecef;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
}

.btn-use-suggested {
  background: #007bff;
  color: white;
  border: none;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-use-suggested:hover:not(:disabled) {
  background: #0056b3;
}

.btn-use-suggested:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sku-input-group {
  position: relative;
}

.sku-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #495057;
}

.required {
  color: #dc3545;
}

.sku-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.sku-input {
  width: 100%;
  padding: 12px 40px 12px 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  transition: all 0.2s;
}

.sku-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.sku-input.error {
  border-color: #dc3545;
}

.sku-input.error:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
}

.sku-input.success {
  border-color: #28a745;
}

.sku-input.success:focus {
  border-color: #28a745;
  box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
}

.sku-input:disabled {
  background-color: #f8f9fa;
  cursor: not-allowed;
}

.validation-status {
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
}

.success-icon {
  color: #28a745;
}

.error-icon {
  color: #dc3545;
}

.sku-format-helper {
  margin-top: 4px;
}

.sku-format-helper small {
  color: #6c757d;
  font-size: 0.75rem;
}

.validation-message {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.875rem;
}

.validation-message.error {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.validation-message.success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.validation-message.info {
  background: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.sku-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
}

.suggestions-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.75rem;
  font-weight: 500;
  color: #6c757d;
}

.suggestion-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: white;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s;
}

.suggestion-item:hover {
  background: #f8f9fa;
}

.suggestion-item code {
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
}

.suggestion-item small {
  color: #6c757d;
  margin-top: 2px;
}

.sku-analytics {
  margin-top: 16px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.sku-analytics details {
  padding: 12px;
}

.sku-analytics summary {
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 8px;
}

.analytics-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.analytics-item {
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
}

.analytics-item:first-child span:first-child {
  color: #6c757d;
}

.analytics-item:first-child span:last-child {
  font-weight: 500;
}

/* Icon placeholder styles */
.icon {
  width: 16px;
  height: 16px;
  display: inline-block;
}
</style> 